import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { catchError, throwError } from 'rxjs';
import * as Sentry from '@sentry/angular';
import { environment } from '../../../environments/environment';

/**
 * Reports failed API calls to Sentry.
 *
 * Mirrors the backend policy (HttpExceptionFilter): only 5xx and network failures are reported.
 * 4xx are client errors — validation, 401, 403, 404 — expected traffic that would drown the signal
 * and burn quota. A no-op when Sentry has no DSN configured.
 *
 * The error is NOT swallowed: it is always re-thrown so the auth interceptor and the calling
 * component keep their existing behaviour.
 */
export const sentryInterceptor: HttpInterceptorFn = (req, next) => {
  return next(req).pipe(
    catchError((error: unknown) => {
      if (environment.sentryDsn && error instanceof HttpErrorResponse) {
        report(error, req.method);
      }
      return throwError(() => error);
    })
  );
};

function report(error: HttpErrorResponse, method: string): void {
  // status 0 = the request never got a response (offline, DNS, CORS, connection refused).
  const isServerError = error.status >= 500;
  const isNetworkFailure = error.status === 0;

  if (!isServerError && !isNetworkFailure) {
    return;
  }

  Sentry.withScope((scope) => {
    scope.setLevel('error');
    scope.setTag('http.status', String(error.status));
    scope.setTag('error.source', isNetworkFailure ? 'network' : 'api');
    // Path only — the query string can carry tokens, and the response body can carry user data.
    scope.setContext('request', { method, url: stripQuery(error.url) });
    // Group by endpoint instead of letting every URL become its own issue.
    scope.setFingerprint(['api', method, stripQuery(error.url) ?? 'unknown', String(error.status)]);
    Sentry.captureException(
      new Error(`HTTP ${error.status} ${method} ${stripQuery(error.url) ?? 'unknown'}`)
    );
  });
}

/** Drop the query string and hash — they can contain tokens or email addresses. */
function stripQuery(url: string | null): string | null {
  if (!url) {
    return null;
  }
  return url.split(/[?#]/)[0];
}
